import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccumulatedBildungsangeboteService } from '../accumulatedBildungsangebote.service';
import { UrlService } from '../state/url.service';

/**
 * Stellt die notwendigen Methoden und Werte bereit, um die Ergebnis-Seite zu durchblättern.
 */
@Injectable({
  providedIn: 'root'
})
export class PaginationService {
  private readonly urlService = inject(UrlService);
  private readonly accumulatedBildungsangeboteService = inject(AccumulatedBildungsangeboteService);

  private readonly _totalResults: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly _totalPages: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly _currentPage: BehaviorSubject<number> = new BehaviorSubject(0);
  private readonly _loadedPages$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private readonly _lowestPage$ = this._loadedPages$.pipe(
    map((loadedPages) => {
      return Math.min(...loadedPages);
    })
  );
  private readonly _highestPage$ = this._loadedPages$.pipe(
    map((loadedPages) => {
      return Math.max(...loadedPages);
    })
  );
  public hasNextPage$ = combineLatest([this._totalPages, this._highestPage$]).pipe(
    map(([totalPages, highestPage]) => totalPages - 1 > highestPage)
  );
  public hasPreviousePage$ = this._lowestPage$.pipe(map((lowestPage) => lowestPage != 0));
  public loadedPages$ = this._loadedPages$.asObservable();
  /**
   * Fasst alle Werte zur Pagination zusammen, um sie aggregiert abrufen zu können.
   */
  public pageInfos$ = combineLatest([
    this._totalResults.asObservable(),
    this._totalPages.asObservable(),
    this._currentPage.asObservable(),
    this._loadedPages$.asObservable()
  ]).pipe(
    map(([totalResults, totalPages, currentPage, loadedPages]) => ({
      totalResults,
      totalPages: totalPages,
      currentPage: currentPage + 1,
      loadedPages
    }))
  );

  constructor() {
    this.accumulatedBildungsangeboteService.bildungsAngebotPages$.subscribe((pages) => {
      const loadedPages = pages.map((page) => page.page.number);
      this._loadedPages$.next(loadedPages);
      this._currentPage.next(pages[0]?.page?.number);
      this._totalResults.next(pages[0]?.page.totalElements ?? 0);
      this._totalPages.next(pages[0]?.page.totalPages ?? 0);
    });
  }

  /**
   * Wahr gdw. die letzte Seite aufgerufen wurde, es aber noch mehr Ergebnisse gibt.
   */
  public showMaxAlert(): boolean {
    return (
      this._totalResults.getValue() > 10000 &&
      this._currentPage.getValue() === this._totalPages.getValue() - 1
    );
  }

  public async nextPage(): Promise<void> {
    const highestPage = await firstValueFrom(this._highestPage$);
    if (highestPage < this._totalPages.getValue() - 1) {
      this.jumpTo(highestPage + 1);
    }
  }

  public async previousPage(): Promise<void> {
    const _lowestPage = await firstValueFrom(this._lowestPage$);
    if (_lowestPage > 0) {
      this.jumpTo(_lowestPage - 1);
    }
  }

  private jumpTo(seite: number): void {
    this.urlService.updateUrlQueryParams({ [UrlService.AKTUELLE_SEITE]: seite });
  }
}
